import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { PackagingMinimisationAssessmentRunResults } from './PackagingMinimisationAssessmentRunResults'
import {
  type PackagingMinimisationAssessmentResult,
  useRunPackagingItemMinimisationAssessment,
} from '@/shared/hooks/api/packagingSuppliers'

type PackagingItemMinimisationRunSheetProps = {
  supplierId: string
  itemId: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function PackagingItemMinimisationRunSheet({
  supplierId,
  itemId,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: PackagingItemMinimisationRunSheetProps) {
  const { t } = useTranslation('packagingSuppliers')
  const [internalOpen, setInternalOpen] = useState(false)
  const [latestResult, setLatestResult] =
    useState<PackagingMinimisationAssessmentResult | null>(null)
  const minimisationMutation = useRunPackagingItemMinimisationAssessment(
    supplierId,
    itemId
  )

  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen

  const setOpen = (nextOpen: boolean) => {
    if (isControlled) {
      controlledOnOpenChange?.(nextOpen)
    } else {
      setInternalOpen(nextOpen)
    }
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setLatestResult(null)
      minimisationMutation.reset()
    }
  }

  const handleRun = async () => {
    setLatestResult(null)
    try {
      const nextResult = await minimisationMutation.mutateAsync()
      setLatestResult(nextResult)
    } catch {
      // isError state drives the sheet error message.
    }
  }

  const handleFooterClick = () => {
    if (latestResult) {
      handleOpenChange(false)
      return
    }
    void handleRun()
  }

  let footerButtonLabel = t('itemDetail.minimisation.runAssessment')
  if (minimisationMutation.isPending) {
    footerButtonLabel = t('itemDetail.minimisation.running')
  } else if (latestResult) {
    footerButtonLabel = t('common:actions.close')
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side='right'
        className='flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl'
      >
        <SheetHeader className='shrink-0 border-b px-6 py-4 pe-10 text-start'>
          <SheetTitle>{t('itemDetail.minimisation.runSheetTitle')}</SheetTitle>
          <SheetDescription>
            {t('itemDetail.minimisation.runSheetDescription')}
          </SheetDescription>
        </SheetHeader>

        <div className='min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4'>
          {minimisationMutation.isError && (
            <p className='text-sm text-destructive'>
              {t('itemDetail.minimisation.runFailed')}
            </p>
          )}

          {latestResult ? (
            <PackagingMinimisationAssessmentRunResults result={latestResult} />
          ) : null}
        </div>

        <SheetFooter className='shrink-0 border-t px-6 py-4 sm:flex-row sm:justify-end'>
          <Button
            disabled={minimisationMutation.isPending}
            onClick={handleFooterClick}
          >
            {footerButtonLabel}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
